import { Box, Grid, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import MaterialTable from "material-table";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { getAllAppointment, getDoctorDetail, updateAppointmentById } from "../../../api/appointmentApi";
import Color from "../../../styles/Color";
import { swalCondition } from "../../../utils/swalHelper";
import InputDatePicker from "../../components/InputDatePicker";
import { getTableColumns, initialTableOptions } from "./appointmentOption";
import FormAppointment from "./components/FormAppointment";
import { useSelector } from "react-redux";

function Appointment() {
    // ref
    const controllerRef = useRef(null);
    const tableRef = useRef(null);

    // global redux state
    const { thisMenuPermission } = useSelector(state => {
        const { menuPermission } = state?.menu;
        return {
            thisMenuPermission: menuPermission?.find(menu => menu?.path === window.location.pathname)?.permission,
        };
    });

    // component state
    const [tableOptions, setTableOptions] = useState(initialTableOptions);
    const [datePick, setDatePick] = useState({ start: new Date(), end: new Date() });
    const [modalEditAppointment, setModalEditAppointment] = useState({ data: {}, isOpen: false });
    const [doctorDetail, setdoctorDetail] = useState([]);

    // fetch data
    const fetchData = useCallback(
        async query => {
            try {
                const queryParams = {
                    page: query?.page + 1 || 1,
                    limit: query?.pageSize || 10,
                    search: query?.search || "",
                    order: query?.orderBy?.field || "appointmentId",
                    direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                    startDate: moment(datePick?.start).format("YYYY-MM-DD"),
                    endDate: moment(datePick?.end).format("YYYY-MM-DD"),
                };
                const res = await getAllAppointment(queryParams, controllerRef.current?.signal);
                if (res) {
                    setTableOptions(currentState => ({
                        ...currentState,
                        currentPage: query?.page,
                        pageSize: query?.pageSize || 10,
                        totalData: res?.pagination?.totalData || 0,
                        order: query?.orderBy?.field || "appointmentId",
                        direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                    }));
                    return {
                        data: res?.data || [],
                        page: query?.page || 0,
                        totalCount: res?.pagination?.totalData || 0,
                    };
                }
                return {
                    data: [],
                    page: 0,
                    totalCount: 0,
                };
            } catch (error) {
                console.dir(error);
                return {
                    data: [],
                    page: 0,
                    totalCount: 0,
                };
            }
        },
        [datePick],
    );

    // handle function action table
    const handleActions = useCallback(
        async (type = "", data = {}) => {
            // cancel button
            if (type === "cancel") {
                const isConfirm = await swalCondition(
                    '<div class="h3 t-text-primary">ต้องการยกเลิกนัดหมายหรือไม่ ?</div>',
                    "",
                    {
                        icon: "error",
                    },
                );
                if (isConfirm) {
                    const isUpdate = await updateAppointmentById(
                        data?.appointmentId,
                        { status: "2" },
                        controllerRef.current?.signal,
                    );
                    if (isUpdate) {
                        tableRef.current.onQueryChange();
                    }
                }
            }
            // confirm button
            if (type === "confirm") {
                const isConfirm = await swalCondition(
                    '<div class="h3 t-text-primary">ต้องการยืนยันการนัดหมายหรือไม่ ?</div>',
                    "",
                    {
                        icon: "warning",
                    },
                );
                if (isConfirm) {
                    const isUpdate = await updateAppointmentById(
                        data?.appointmentId,
                        { status: "1" },
                        controllerRef.current?.signal,
                    );
                    if (isUpdate) {
                        tableRef.current.onQueryChange();
                    }
                }
            }
            // confirm edit
            if (type === "edit") {
                if (data?.rights === "1") {
                    try {
                        const res = await getDoctorDetail();
                        const findDoctor = res.find(item => data?.clinic === item?.Department);
                        setdoctorDetail(
                            findDoctor?.Doctorprofile.map(item => ({
                                title: item?.Doctorname,
                                value: item?.Doctorname,
                            })),
                        );
                    } catch (error) {
                        console.log(error);
                    }
                }

                setModalEditAppointment({ isOpen: true, data, doctorDetail });
            }
        },
        [doctorDetail],
    );

    // close edit appointment modal
    const handleCloseModalEditAppointment = useCallback(() => {
        setModalEditAppointment({ isOpen: false, data: {} });
    }, []);

    const handleSubmitEditAppointment = useCallback(async values => {
        const isUpdate = await updateAppointmentById(
            values?.appointmentId,
            {
                appointDate: values?.date,
                doctor: values?.doctor,
                appointTime: values?.time,
            },
            controllerRef.current?.signal,
        );
        if (isUpdate) {
            tableRef.current.onQueryChange();
            setModalEditAppointment({ isOpen: false, data: {} });
        }
    }, []);

    // onChange start date
    const onChangeStartDate = useCallback(date => {
        setDatePick(currentState => ({ ...currentState, start: date }));
    }, []);

    // onChange end date
    const onChangeEndDate = useCallback(date => {
        setDatePick(currentState => ({ ...currentState, end: date }));
    }, []);

    // memo
    const tableColumns = useMemo(() => getTableColumns({ handleActions, thisMenuPermission }), [handleActions, thisMenuPermission]);

    // run only first render
    useEffect(() => {
        controllerRef.current = new AbortController();
        // to avoid memory leaked.
        return () => {
            controllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (datePick.start > datePick.end) {
            setDatePick(currentState => ({ ...currentState, end: datePick.start }));
        }
        tableRef.current.onQueryChange();
    }, [datePick]);

    return (
        <div>
            <Box>
                <Grid container>
                    <Grid item xs={12} className="mb-3">
                        <h1 style={{ color: Color.primary }}>Appointments</h1>
                    </Grid>
                    <Grid item xs={12} md={9} lg={6} className="mb-3">
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <InputDatePicker
                                    label=""
                                    inputProps={{ className: "t-form-group-custom-shadow" }}
                                    className="bg-white rounded"
                                    value={datePick.start}
                                    onChange={onChangeStartDate}
                                    size="medium"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <InputDatePicker
                                    label=""
                                    inputProps={{ className: "t-form-group-custom-shadow" }}
                                    className="bg-white rounded"
                                    value={datePick.end}
                                    minDate={datePick.start}
                                    onChange={onChangeEndDate}
                                    size="medium"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className="mb-3">
                        <div className="t-table-custom">
                            <MaterialTable
                                tableRef={tableRef}
                                title=""
                                data={fetchData}
                                className="table-responsive"
                                style={{ width: "100%", borderRadius: 10 }}
                                columns={tableColumns}
                                options={tableOptions}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12} className="mb-3">
                        <Modal
                            show={modalEditAppointment.isOpen}
                            onHide={handleCloseModalEditAppointment}
                            scrollable
                            centered
                            size="lg">
                            <Modal.Header>
                                <div></div>
                                <IconButton onClick={handleCloseModalEditAppointment} size="small">
                                    <Close />
                                </IconButton>
                            </Modal.Header>
                            <Modal.Body>
                                <h3 className="text-center mb-5">Edit Appointment</h3>
                                <FormAppointment
                                    data={modalEditAppointment?.data}
                                    doctor={doctorDetail}
                                    onSubmit={handleSubmitEditAppointment}
                                />
                            </Modal.Body>
                        </Modal>
                    </Grid>
                </Grid>
            </Box>
        </div>
    );
}

export default Appointment;
